/**
 * Permission codes the Fixed Assets screens declare an interest in.
 *
 * They are the dot-strings `internal/assets/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once
 * here means a renamed permission is a compile error rather than a menu item
 * that silently disappears.
 *
 * The block below the module's own codes is the permissions this module reads
 * *other* modules through. A fixed asset is only ever half of the story: the
 * money it capitalizes was posted by AP, the journals it produces belong to GL,
 * and the periods it depreciates into are owned by the organization module. A
 * user who may read the register but not the ledger sees the register and is
 * told what they cannot see, rather than being shown an empty panel that
 * implies nothing was ever posted.
 *
 * None of these is registered `RequiresMFA`. §13's step-up list is payments,
 * bank-detail changes and period close specifically — see the note on
 * `assets/permissions.go`'s `init()`, which explains why a disposal is not on
 * it even though it posts a gain or loss.
 */

export const ASSETS_PERMISSIONS = {
  classRead: 'assets.class.read',
  classManage: 'assets.class.manage',

  assetRead: 'assets.asset.read',
  assetCreate: 'assets.asset.create',
  assetUpdate: 'assets.asset.update',
  assetTransfer: 'assets.asset.transfer',
  assetRevalue: 'assets.asset.revalue',
  assetDispose: 'assets.asset.dispose',
  /** Move an asset to another legal entity of the tenant (FT9-B36). */
  transferIntercompany: 'assets.transfer.intercompany',

  depreciationRun: 'assets.depreciation.run',

  reportRead: 'assets.report.read',

  /**
   * Write the disposal approval threshold (`PUT /asset-settings/{companyId}`).
   * CFO/Tenant Admin only. Tightening applies at once; loosening (raising or
   * clearing it) waits as a pending change for a second person's approval.
   */
  disposalSettingsManage: 'assets.disposal.settings.manage',

  /* ------------------------------------------- C3 parallel books (§6.1) */

  bookRead: 'assets.book.read',
  bookManage: 'assets.book.manage',
  /** A prospective method, life or salvage change — never a restatement. */
  bookChange: 'assets.book.change_method',

  /** Split an asset into components, apportioning its cost to the cent. */
  componentise: 'assets.asset.componentise',

  impairmentAssess: 'assets.impairment.assess',
  /** Step-up MFA: an impairment reduces reported profit. */
  impairmentPost: 'assets.impairment.post',

  verificationRead: 'assets.verification.read',
  verificationManage: 'assets.verification.manage',

  /** Scope every screen to a company, and name the one an asset belongs to. */
  companyRead: 'org.company.read',
  /** The chart of accounts behind a class's four accounts and a credit account. */
  accountsRead: 'org.account.read',
  /** Branches and cost centres, the dimensions an asset's charge is tagged with. */
  unitRead: 'org.unit.read',
  /** Fiscal periods — which period a depreciation run would land in, and its status. */
  fiscalRead: 'org.fiscal.read',
  /** The journals every acquisition, run, revaluation and disposal produced. */
  journalRead: 'gl.journal.read',
  /** The posted supplier invoice a capitalized asset was born from. */
  apInvoiceRead: 'ap.invoice.read',
} as const

export type AssetsPermission = (typeof ASSETS_PERMISSIONS)[keyof typeof ASSETS_PERMISSIONS]
